import {
  REVENUE_CHECK_CATEGORY_GUIDE,
  type RevenueCheckItem,
  type RevenueCheckSeverity,
  type RevenueChecksResponse,
} from '@cortex/domain-ac-mens-hub-api';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatSnapshotJST, formatYen } from '../../../lib/revenue-aggregation.js';

/** 重大度の並び順（即時対応を上に）。 @graph-connects none */
const SEVERITY_ORDER: readonly RevenueCheckSeverity[] = ['critical', 'warning', 'info'];

/** 重大度の表示名の翻訳キーと、行の左端の色。 @graph-connects none */
const SEVERITY_STYLE: Record<RevenueCheckSeverity, { labelKey: string; bar: string }> = {
  critical: { labelKey: 'revenue.checks.severityImmediate', bar: 'border-l-[var(--color-error)]' },
  warning: { labelKey: 'revenue.checks.severityPeriodic', bar: 'border-l-[var(--color-warning)]' },
  info: { labelKey: 'revenue.checks.severityRecord', bar: 'border-l-[var(--color-border)]' },
};

/** @graph-connects none */
const TH =
  'border-b border-[var(--color-border)] px-3 py-2 text-left text-xs font-medium whitespace-nowrap text-[var(--color-text-secondary)]';
/** @graph-connects none */
const TD = 'border-b border-[var(--color-border)] px-3 py-2 align-top text-sm';

/** @graph-connects none */
function CountCell({ item }: { item: RevenueCheckItem }) {
  const { t } = useTranslation();
  if (item.count === null) return <td className={TD} />;
  const increased = item.previousCount !== null && item.count > item.previousCount;
  return (
    <td className={`${TD} tabular-nums`}>
      <div className="text-right">{item.count.toLocaleString('ja-JP')}</div>
      {item.previousCount !== null && (
        <div
          className={`text-right text-[10px] ${increased ? 'font-semibold text-[var(--color-error)]' : 'text-[var(--color-text-muted)]'}`}
        >
          {increased
            ? t('revenue.checks.increased', {
                previous: item.previousCount.toLocaleString('ja-JP'),
              })
            : t('revenue.checks.previous', {
                previous: item.previousCount.toLocaleString('ja-JP'),
              })}
        </div>
      )}
    </td>
  );
}

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上集計（正式版）の要対応タブ。検算・検知の違反と締め後の差を、重大度・何が問題か・推奨アクション・担当で並べ、0件なら対応不要と出す。
 * @graph-connects REVENUE_CHECK_CATEGORY_GUIDE [references] 分類ごとの問題・推奨アクション・担当
 */
export function LedgerChecksView({ data }: { data: RevenueChecksResponse }) {
  const { t } = useTranslation();
  const items = [...data.items].sort(
    (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity),
  );

  return (
    <div className="mt-6 space-y-3">
      {data.run !== null && (
        <p className="text-xs text-[var(--color-text-muted)]">
          {t('revenue.checks.runAt', { time: formatSnapshotJST(data.run.computedAt) })}
        </p>
      )}
      {items.length === 0 ? (
        <Card className="flex items-center gap-2 p-6">
          <CheckCircle2 className="h-5 w-5 text-[var(--color-accent-primary)]" aria-hidden="true" />
          <p className="text-sm text-[var(--color-text-secondary)]">{t('revenue.checks.empty')}</p>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-[var(--color-border)]">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-[var(--color-bg-base)]">
                <th className={TH}>{t('revenue.checks.severity')}</th>
                <th className={TH}>{t('revenue.checks.problem')}</th>
                <th className={TH}>{t('revenue.checks.action')}</th>
                <th className={TH}>{t('revenue.checks.owner')}</th>
                <th className={`${TH} text-right`}>{t('revenue.checks.count')}</th>
                <th className={`${TH} text-right`}>{t('revenue.checks.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const guide = REVENUE_CHECK_CATEGORY_GUIDE[item.category];
                const style = SEVERITY_STYLE[item.severity];
                return (
                  <tr
                    key={`${item.checkId}::${String(item.baseCheckId)}::${String(item.month)}::${String(item.asOfDate)}`}
                    className={`border-l-4 ${style.bar}`}
                  >
                    <td className={`${TD} whitespace-nowrap font-medium`}>{t(style.labelKey)}</td>
                    <td className={TD}>
                      <div className="font-medium text-[var(--color-text-primary)]">
                        {item.month === null
                          ? guide.label
                          : `${guide.label}（${item.asOfDate ?? item.month}）`}
                      </div>
                      <div className="text-xs text-[var(--color-text-secondary)]">
                        {guide.problem}
                      </div>
                      {item.detail !== null && (
                        <code className="mt-1 block text-[10px] break-all text-[var(--color-text-muted)]">
                          {item.detail}
                        </code>
                      )}
                    </td>
                    <td className={`${TD} text-[var(--color-text-secondary)]`}>{guide.action}</td>
                    <td className={`${TD} whitespace-nowrap text-[var(--color-text-secondary)]`}>
                      {guide.owner}
                    </td>
                    <CountCell item={item} />
                    <td className={`${TD} text-right tabular-nums`}>
                      {item.amount === null ? '' : formatYen(item.amount)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
